import {
  Loader,
  MyIcon,
  useToastMessage,
  useUserPermission
} from 'ccs-ui-components';
import { useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { DEFAULT_LIST_SIZE, DEFAULT_PAGE_NUMBER, EVENT_ROUTE, EventLabels, SORT_STRINGS, URL_PATH } from '../../../../control-plane/constants';
import { IEvent, IService } from '../../../../control-plane/models';
import { REGISTRY_TYPE } from '../../../../dashboard/constants/CommonConstants';
import { journeyBuilderRoutes } from '../../../../journey-builder/constants/JdRoutes';
import { JourneyListState } from '../../../../journey-builder/modules/journey-list/Utility';
import {
  RESPONSE_CODE,
  STATUS_TYPES
} from '../../../constants/StatusConstants';
import useConfirmDialog from '../../../hooks/ConfirmationDialog';
import useDeleteEntity from '../../../hooks/useDeleteEntity';

import useServiceEventAssociation from '../../../../control-plane/hooks/events/association-list/useServiceEventAssociation';
import useSourceEventAssociation from '../../../../control-plane/hooks/events/association-list/useSourceEventAssociation';
import useServiceDependencyGraph from '../../../../control-plane/hooks/services/useServiceDependencyGraph';
import classes from './DropdownMenuButton.module.scss';
import { canEditRegistry, canMarkAsInactive } from './helpers';

interface IDropDownMenuProps {
  status: string;
  deleteURL: string;
  registryName?: string;
  onDeleteSuccess?: () => void;
  onDependencyGraphFound?: (isFound: boolean, entityName: string, entityVersion: string, entityStatus: string) => void;
  showOn?: string;
  entityData?: IEvent | IService | JourneyListState;
  registryType?: string;
}

const DropdownMenuButton = ({ registryName = '', status, deleteURL, onDeleteSuccess = () => { }, onDependencyGraphFound = undefined, showOn = '', entityData = undefined, registryType = '' }: IDropDownMenuProps) => {
  const { deleteEntity } = useDeleteEntity();
  const { getServiceDependencyGraph } = useServiceDependencyGraph();
  const { serviceEventAssociation } = useServiceEventAssociation();
  const { sourceEventAssociation } = useSourceEventAssociation();
  const { t: translate } = useTranslation();
  const { renderToast, success, failure } = useToastMessage();
  const [isDeleting, setIsDeleting] = useState(false);
  const { show: showDialog, render: renderDialog } = useConfirmDialog();
  const history = useNavigate();
  const { permissionObject } = useUserPermission();
  const route = useLocation();

  const REGISTRY_LIST = [
    REGISTRY_TYPE.SERVICE,
    REGISTRY_TYPE.EVENTS,
    REGISTRY_TYPE.JOURNEY
  ];

  const isEdit = canEditRegistry(registryType, permissionObject);

  const isInactiveAllowed = canMarkAsInactive(registryType, permissionObject);

  const callDeleteActiveEntityApi = async () => {
    setIsDeleting(true);
    try {
      const { statusCode, deleteResponse, errorMessage } = await deleteEntity(`${deleteURL}`);
      if (statusCode === RESPONSE_CODE.DELETE_SUCCESS) {
        success(
          translate('inactive_records.success_title'),
          translate('inactive_records.msg_delete_success')
        );
        if (onDeleteSuccess) onDeleteSuccess();
      } else {
        const error = deleteResponse?.errorMessage ?? errorMessage;
        failure(
          translate('inactive_records.lbl_delete_fail'),
          `${error}`
        );
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const callOnDependencyGraphFound = (isFound: boolean) => {
    if (onDependencyGraphFound) {
      onDependencyGraphFound(isFound, `${registryName}`, `${entityData?.version}`, `${entityData?.status}`);
    }
  };

  const checkServicesAndSourceAssociation = async () => {
    const queryParam = {
      page: `${DEFAULT_PAGE_NUMBER}`,
      item: `${DEFAULT_LIST_SIZE}`,
      sort: EventLabels.LAST_MODIFIED,
      type: SORT_STRINGS.DESC,
    };

    // Check for Service association
    const { serviceAssociationRecords, isError } = await serviceEventAssociation(queryParam, `${registryName}`, `${entityData?.version}`);

    if (isError) {
      failure(
        translate('inactive_records.lbl_delete_fail'),
        translate('info_message.msg_event_error_association')
      );
    } else if (serviceAssociationRecords?.services?.length) {
      callOnDependencyGraphFound(true);
    } else {
      // check for source association
      const { sourceRecords, isError } = await sourceEventAssociation(queryParam, `${registryName}`, `${entityData?.version}`);
      if (isError) {
        failure(
          translate('inactive_records.lbl_delete_fail'),
          translate('info_message.msg_event_error_association')
        );
      } else if (sourceRecords?.sources?.length) {
        callOnDependencyGraphFound(true);
      } else {
        callOnDependencyGraphFound(false);
        callDeleteActiveEntityApi();
      }
      setIsDeleting(false);
    }
  };

  const checkServiceAssociation = async () => {
    try {
      setIsDeleting(true);
      const { statusCode, graphResponse } = await getServiceDependencyGraph(
        registryName ?? '',
        `${entityData?.version}`,
        `${DEFAULT_PAGE_NUMBER}`,
        DEFAULT_LIST_SIZE,
        SORT_STRINGS.DESC,
        undefined
      );
      if (statusCode === RESPONSE_CODE.POST_SUCCESS) {
        if (
          graphResponse?.totalJourneysCount
          && graphResponse?.totalJourneysCount > 0
        ) {
          callOnDependencyGraphFound(true);
        } else {
          callOnDependencyGraphFound(false);
          callDeleteActiveEntityApi();
        }
      } else {
        failure(
          translate('inactive_records.lbl_delete_fail'),
          translate('info_message.msg_service_error_association')
        );
      }
    } finally {
      setIsDeleting(false);
    }
  };

  /**
   * @method deleteActiveEntity
   * @description deletes the active entity
   */
  const deleteActiveEntity = async () => {
    showDialog({
      title: translate('inactive_records.msg_mark_inactive_header'),
      message: translate('inactive_records.msg_mark_inactive'),
      cancelText: translate('confirm-dialog-prompt-box.lbl_close'),
      okText: translate('inactive_records.confirm_mark_inactive'),
      testId: 'confirm_dialog'
    }).then(async (confirm) => {
      if (confirm) {
        // call delete API
        if (
          registryType === REGISTRY_TYPE.SERVICE
          && status?.toLocaleLowerCase() === STATUS_TYPES.ACTIVE
        ) {
          checkServiceAssociation();
        } else if (
          registryType === REGISTRY_TYPE.EVENTS
          && status?.toLocaleLowerCase() === STATUS_TYPES.ACTIVE
        ) {
          checkServicesAndSourceAssociation();
        } else {
          callDeleteActiveEntityApi();
        }
      }
    });
  };

  /**
   * @method deleteActiveEntity
   * @description deletes the active entity
   */

  const cloneClickHandler = () => {
    showDialog({
      message: translate('inactive_records.clone_message'),
      title: `${translate('inactive_records.clone_record')}-${registryName}`,
      cancelText: translate('confirm-dialog-prompt-box.lbl_close'),
      okText: translate('confirm-dialog-prompt-box.lbl_proceed')
    }).then((confirmStatus) => {
      if (confirmStatus) {
        openRegistry();
      }
    });
  };
  /**
   * Navigates to create clone registry page
   */
  const openRegistry = () => {
    if (registryType === REGISTRY_TYPE.EVENTS) {
      history(EVENT_ROUTE.CREATE_EVENT_ROUTE, {
        state: { cloneEventName: registryName, version: entityData?.version, path: route?.search ?? '' }
      });
    }
    if (registryType === REGISTRY_TYPE.SERVICE) {
      history(`${URL_PATH.SERVICE_HOME}${URL_PATH.CREATE_SERVICE}`, {
        state: { cloneServiceName: registryName, version: entityData?.version, path: route?.search ?? '' }
      });
    }
    if (registryType === REGISTRY_TYPE.JOURNEY) {
      history(journeyBuilderRoutes.createJourney, {
        state: {
          cloneJourneyName: registryName,
          cloneJourneyVersion: entityData?.version
        }
      });
    }
  };

  const registryStatus = status.toLocaleUpperCase();

  const isMarkAsInactiveDisabled = !isInactiveAllowed
    || (registryStatus !== STATUS_TYPES.ACTIVE && (registryType === REGISTRY_TYPE.SOURCE || registryType === REGISTRY_TYPE.JOURNEY))
    || ((registryType === REGISTRY_TYPE.SERVICE
      || registryType === REGISTRY_TYPE.EVENTS)
      && (registryStatus === STATUS_TYPES.PENDING_APPROVAL
        || registryStatus === STATUS_TYPES.INACTIVE));

  return (
    <div>
      <Loader show={isDeleting} />
      {renderToast()}
      {renderDialog()}
      <Dropdown className={classes.container} data-testid="dropdownMenu">
        <Dropdown.Toggle
          title="More"
          style={{
            visibility: 'visible'
          }}
          className={classes[!showOn?.length ? 'btn-more-view' : 'btn-more']}
          variant="ternary"
          data-testid="dropdownBtn"
        >
          <i className="bi bi-three-dots-vertical" />
        </Dropdown.Toggle>

        <Dropdown.Menu>
          <Dropdown.Item
            data-testid="markInactiveBtn"
            key="markInactiveBtn"
            onClick={deleteActiveEntity}
            disabled={isMarkAsInactiveDisabled}
          >
            <MyIcon name="inactiveIcon" />{' '}
            {translate('inactive_records.mark_inactive')}
          </Dropdown.Item>
          {registryType && REGISTRY_LIST.includes(registryType) && (
            <Dropdown.Item
              data-testid="cloneBtn"
              key="cloneBtn"
              onClick={cloneClickHandler}
              disabled={
                !isEdit || status.toLocaleUpperCase() === STATUS_TYPES.INACTIVE
              }
            >
              <MyIcon name="cloneIcon" />{' '}
              {translate('inactive_records.clone_record')}
            </Dropdown.Item>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};
export default DropdownMenuButton;
